import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  Typography
} from '@mui/material';
import dayjs from 'dayjs';
import { doc, getDoc } from 'firebase/firestore';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { db } from '../../api/firebase/config';
import {
  companiesRef,
  getAgentFromSearch
} from '../../api/firebase/firestore/company';
import { issuesRef, submitIssue } from '../../api/firebase/firestore/issue';
import { clientSchema } from '../../api/firebase/firestore/issue/validation';
import { usersRef } from '../../api/firebase/firestore/user';
import { sendMailIssueAgent, sendMailIssueClient } from '../../api/sendgrid';
import {
  consult,
  kpi,
  mediaType,
  menu,
  option
} from '../../constants/CheckboxList';
import {
  black,
  blackText,
  blue,
  blueGrayText,
  cardBorder,
  gray,
  primaryButtonBackground,
  primarycolor
} from '../../constants/Colors';
import {
  buttonFont,
  cardContentPadding,
  cardSize,
  formWideMb,
  textFontSize,
  textMb,
  titleFontSize,
  titleMb
} from '../../constants/Location';
import { clientSteps } from '../../constants/RegisterStep';
import { budget, purpose, useIndustry } from '../../constants/Select';
import { screenType, select } from '../../constants/enum';
import { AuthContext } from '../../contexts/auth';
import { throwError } from '../../modules/error';
import { sendSlackIssueAgent, sendSlackIssueClient } from '../../modules/slack';

import CustomCheckbox from './CustomCheckbox';
import CustomDatePicker from './CustomDatePicker';
import CustomGuideButton from './CustomGuideButton';
import CustomLoading from './CustomLoading';
import CustomRedioButton from './CustomRadioButton';
import CustomSelectField from './CustomSelectField';
import CustomSnackbar from './CustomSnackbar';
import CustomStepper from './CustomStepper';
import CustomTextfield from './CustomTextfield';

import type { SelectOption } from '../../constants/Select';
import type { Dayjs } from 'dayjs';
import type { DocumentData } from 'firebase/firestore';

export interface ClientRegisterIssueType {
  type: string | null;
  client: number | string | null;
  name: string | null;
  genre: number;
  genreOther: string | null;
  productInformation: string | null;
  merchandiseType: boolean | null;
  purpose1: number;
  purpose1Other?: string | null;
  purpose2: number;
  purpose2Other?: string | null;
  purpose3: number;
  purpose3Other?: string | null;
  kpi?: boolean[];
  kpiOtherText?: string;
  targetClient?: string | null;
  targetArea?: string | null;
  startDateCheck?: boolean[];
  startDate?: Dayjs | null;
  applicationDateFrom?: Dayjs | null;
  applicationDateTo?: Dayjs | null;
  budgetCheck?: boolean[];
  budget?: number | null | undefined;
  mediaTypeCheck?: boolean[];
  mediaType?: boolean[];
  menuCheck?: boolean[];
  menu?: boolean[];
  menuOther?: string | null;
  materialFlg?: boolean | null;
  castingFlg?: boolean | null;
  option?: boolean[];
}

const styles = {
  aboutText: {
    alignItems: 'center',
    borderLeft: 'solid 2px #222',
    boxSizing: 'border-box',
    color: black,
    display: 'flex',
    fontSize: '16px',
    height: '20px',
    justifyContent: 'left',
    mb: '16px',
    paddingLeft: '8px'
  },
  buttonFont,
  cardBorder,
  cardContentPadding,
  cardSize,
  circle: {
    bgcolor: '#DBDBE0',
    borderRadius: '50%',
    height: '64px',
    marginBottom: '10px',
    width: '64px'
  },
  formWideMb,
  issueDescription: {
    color: blueGrayText,
    fontSize: '14px',
    marginBottom: '24px'
  },
  label: {
    fontSize: '12px'
  },
  required: {
    color: primarycolor,
    fontSize: '12px',
    marginLeft: '10px'
  },
  skipButton: {
    height: '36px',
    width: '140px'
  },
  submitButton: {
    '@media screen and (max-width:960px)': {
      height: '42px'
    }
  },
  textFontSize,
  textMb,
  title: {
    color: blackText,
    fontSize: titleFontSize
  },
  titleMb,
  uploadButton: {
    '&:hover': {
      backgroundColor: black
    },
    backgroundColor: black,
    height: '36px',
    marginLeft: '11px',
    minWidth: '157px'
  }
};

interface Props {
  issueId?: string;
  copyIssueId?: string;
  screen: number;
}

export interface timestamp {
  nanoseconds: number;
  seconds: number;
  start: {
    nanoseconds: number;
    seconds: number;
  };
  end: {
    nanoseconds: number;
    seconds: number;
  };
}

interface List {
  value: number | string;
  option: string;
}

const RegisterIssueCard: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const steps = clientSteps();
  const navigate = useNavigate();
  const { issueId, copyIssueId, screen } = props;
  const [issueData, setIssueData] = useState<DocumentData>();
  const [renderingCount, setRenderingCount] = useState<number>(0);

  const url = new URL(window.location.href);
  const param = url.searchParams;
  const type = param.get('type')?.split('?').join('') ?? '';
  const { currentUser } = useContext(AuthContext);

  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [budgetDisabled, setBudgetDisabled] = useState<boolean>(false);
  const [startDateDisabled, setStartDateDisabled] = useState<boolean>(false);
  const purposeList = purpose();
  const genreList = useIndustry();
  const budgetList = budget();
  const [purpose1List, setPurpose1List] = useState<SelectOption[]>([]);
  const [purpose2List, setPurpose2List] = useState<SelectOption[]>([]);
  const [purpose3List, setPurpose3List] = useState<SelectOption[]>([]);

  const locationState = useLocation();
  const currentPage = location.pathname;
  const registerIssue = '/auth/issue';

  // 代理店経由で登録した広告主のID、代理店のissueに紐づく広告主のID
  const clientCompanyId: string | null =
    (locationState.state as { id: string })?.id ?? null;

  const {
    control,
    watch,
    register,
    setValue,
    reset,
    handleSubmit,
    clearErrors,
    formState: { errors }
  } = useForm<ClientRegisterIssueType>({
    defaultValues: {
      applicationDateFrom: null,
      applicationDateTo: null,
      budget: 0,
      castingFlg: null,
      client: clientCompanyId ?? 0,
      genre: 0,
      materialFlg: null,
      merchandiseType: null,
      purpose1: 0,
      purpose2: 0,
      purpose3: 0,
      targetArea: null,
      targetClient: null
    },
    resolver: yupResolver(clientSchema(screen))
  });

  const [firstSubmit, setFirstSubmit] = useState(false);

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        (screen === screenType.agent &&
          (watch('client') === undefined || 0 || Boolean(!watch('client')))) ||
        watch('name') === undefined ||
        '' ||
        watch('genre') === 0 ||
        (watch('genre') === select.other && Boolean(!watch('genreOther'))) ||
        watch('productInformation') === undefined ||
        '' ||
        watch('merchandiseType') === null ||
        watch('purpose1') === 0 ||
        (watch('purpose1') === select.other &&
          Boolean(!watch('purpose1Other'))) ||
        (watch('purpose2') === select.other &&
          Boolean(!watch('purpose2Other'))) ||
        (watch('purpose3') === select.other &&
          Boolean(!watch('purpose3Other'))) ||
        (watch('kpi.10') && Boolean(!watch('kpiOtherText'))) ||
        watch('targetClient') === undefined ||
        '' ||
        (!watch('startDateCheck.1') && watch('startDate') === null) ||
        (!watch('budgetCheck.1') && watch('budget') === 0) ||
        (watch('menu.99') && Boolean(!watch('menuOther'))) ||
        Boolean(errors.name) ||
        Boolean(errors.genre) ||
        (watch('genre') === select.other && Boolean(errors.genreOther)) ||
        Boolean(errors.productInformation) ||
        Boolean(errors.merchandiseType) ||
        Boolean(errors.purpose1) ||
        (watch('purpose1') === select.other && Boolean(errors.purpose1Other)) ||
        Boolean(errors.purpose2) ||
        (watch('purpose2') === select.other && Boolean(errors.purpose2Other)) ||
        Boolean(errors.purpose3) ||
        (watch('purpose3') === select.other && Boolean(errors.purpose3Other)) ||
        (!watch('kpi.10') && Boolean(errors.kpi)) ||
        (watch('kpi.10') && Boolean(errors.kpiOtherText)) ||
        Boolean(errors.targetClient) ||
        Boolean(errors.targetArea) ||
        Boolean(errors.startDateCheck) ||
        Boolean(errors.startDate) ||
        Boolean(errors.applicationDateFrom) ||
        Boolean(errors.applicationDateTo) ||
        Boolean(errors.budgetCheck) ||
        Boolean(errors.budget) ||
        (!watch('mediaTypeCheck.1') && Boolean(errors.mediaType)) ||
        (!watch('menuCheck.1') && Boolean(errors.menu)) ||
        (watch('menu.99') && Boolean(errors.menuOther)) ||
        Boolean(errors.materialFlg) ||
        Boolean(errors.castingFlg) ||
        Boolean(errors.option)
      );
    }
    return false;
  };

  const purposeOtherReset = (): void => {
    if (
      watch('purpose1') !== select.other &&
      watch('purpose2') !== select.other &&
      watch('purpose3') !== select.other
    ) {
      setValue('purpose1Other', null);
      setValue('purpose2Other', null);
      setValue('purpose3Other', null);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    if (!currentUser) {
      setOpen(true);
      setErrorText(t('form.loginError'));
      setTimeout(() => {
        navigate('/auth/login');
      }, 6000);
    }
  }, []);

  const [issueCompanyId, setIssueCompanyId] = useState<string>('');
  const [issueAgentId, setIssueAgentId] = useState<string>('');

  // 自社の与件ではないときは前のページに戻る
  useEffect(() => {
    void (async () => {
      const userRef = doc(usersRef, currentUser?.uid);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      if (userData) {
        const userCompany = userData.companyId as string;

        // 広告主のとき
        if (screen === screenType.client && issueCompanyId) {
          if (userCompany !== issueCompanyId) {
            navigate('/auth/login');
          }
        }

        // 代理店のとき
        if (screen === screenType.agent && issueAgentId) {
          if (userCompany !== issueAgentId) {
            navigate('/auth/login');
          }
        }

        // 代理店経由で登録した与件は広告主は編集不可
        if (
          screen === screenType.client &&
          issueAgentId !== '' &&
          issueAgentId !== null &&
          issueAgentId !== undefined
        ) {
          if (userCompany !== issueCompanyId) {
            navigate('/auth/login');
          }
        }
      }
    })();
  }, [issueCompanyId, issueAgentId]);

  const [clientList, setClientList] = useState<List[]>([]);

  useEffect(() => {
    void (async () => {
      const userRef = doc(usersRef, currentUser?.uid);
      const userSnap = await getDoc(userRef);
      const companyId = userSnap.data()?.companyId as string;
      const response = await getAgentFromSearch(companyId);
      setClientList(response);
    })();
  }, []);

  useEffect(() => {
    void (async () => {
      if (issueId) {
        const issueDocRed = doc(issuesRef, issueId);
        const docSnap = await getDoc(issueDocRed);
        setIssueData(docSnap.data());
      } else if (copyIssueId) {
        const issueDocRed = doc(issuesRef, copyIssueId);
        const docSnap = await getDoc(issueDocRed);
        setIssueData(docSnap.data());
      }
    })();
    setPurpose1List(purposeList);
    setPurpose2List(purposeList);
    setPurpose3List(purposeList);
  }, []);

  useEffect(() => {
    if (issueData) {
      const createNumberArray = (data: number[], newArray: number[]): void => {
        data.forEach((element: number) => {
          newArray.push(element);
        });
      };

      const checkboxBooleanArray = (
        data: number[],
        length?: number
      ): boolean[] => {
        if (length) {
          const newArray = [];
          for (let i = 0; i <= length; i++) {
            newArray.push(false);
          }
          data.forEach((element: number) => {
            newArray[element] = true;
          });
          return newArray;
        }
        return [];
      };

      setIssueCompanyId(issueData.companyId as string);
      setIssueAgentId(issueData.agentId as string);

      const purposeData: number[] = [];
      createNumberArray(issueData.purpose as number[], purposeData);
      reset({
        applicationDateFrom: (issueData.applicationDate as timestamp).start
          ? dayjs.unix((issueData.applicationDate as timestamp).start.seconds)
          : null,
        applicationDateTo: (issueData.applicationDate as timestamp).end
          ? dayjs.unix((issueData.applicationDate as timestamp).end.seconds)
          : null,
        budget: (issueData.budget as number) ? (issueData.budget as number) : 0,
        budgetCheck: [false, Boolean(!issueData.budget)],
        castingFlg: issueData.castingFlg as boolean,
        genre: issueData.genre as number,
        genreOther: issueData.genreOther as string | null,
        kpi: checkboxBooleanArray(
          issueData.kpi as number[],
          watch('kpi')?.length
        ),
        kpiOtherText: issueData.kpiOther as string,
        materialFlg: issueData.materialFlg as boolean,
        mediaType: checkboxBooleanArray(
          Array.isArray(issueData.mediaType)
            ? (issueData.mediaType as number[])
            : [],
          watch('mediaType')?.length
        ),
        mediaTypeCheck: Array.isArray(issueData.mediaType) ? [] : [false, true],
        menu: checkboxBooleanArray(
          Array.isArray(issueData.menu) ? (issueData.menu as number[]) : [],
          watch('menu')?.length
        ),
        menuCheck: Array.isArray(issueData.menu) ? [] : [false, true],
        menuOther: (issueData.menuOther as string) || null,
        merchandiseType: issueData.merchandiseType as boolean,
        name: issueData.name as string | null,
        option: checkboxBooleanArray(
          issueData.option as number[],
          watch('option')?.length
        ),
        productInformation: issueData.productInformation as string | null,
        purpose1: purposeData[0],
        purpose1Other: issueData.purposeOther as string,
        purpose2: purposeData[1] || 0,
        purpose2Other: issueData.purposeOther as string,
        purpose3: purposeData[2] || 0,
        purpose3Other: issueData.purposeOther as string,
        startDate: issueData.startDate
          ? dayjs.unix((issueData.startDate as timestamp).seconds)
          : null,
        startDateCheck: [false, Boolean(!issueData.startDate)],
        ...(screen === screenType.agent && {
          client: issueData.companyId as string | null
        }),
        targetArea: issueData.targetArea as string | null,
        targetClient: issueData.targetClient as string | null
      });
    }
  }, [issueData]);

  useEffect(() => {
    setValue('budget', 0);
    setBudgetDisabled(watch('budgetCheck.1'));
    clearErrors('budget');
  }, [watch('budgetCheck.1')]);

  useEffect(() => {
    setValue('startDate', null);
    setStartDateDisabled(watch('startDateCheck.1'));
    clearErrors('startDate');
  }, [watch('startDateCheck.1')]);

  const [mediaTypeDisabled, setMediaTypeDisabled] = useState<boolean>(false);
  const [menuDisabled, setMenuDisabled] = useState<boolean>(false);

  useEffect(() => {
    setMediaTypeDisabled(watch('mediaTypeCheck.1'));
    if (watch('mediaTypeCheck.1')) {
      setValue('mediaType', []);
    }
  }, [watch('mediaTypeCheck.1')]);

  useEffect(() => {
    setMenuDisabled(watch('menuCheck.1'));
    if (watch('menuCheck.1')) {
      setValue('menu', []);
    }
  }, [watch('menuCheck.1')]);

  useEffect(() => {
    if (renderingCount === 0 || renderingCount === 1) {
      setRenderingCount(renderingCount + 1);
    } else {
      watch('kpi')?.forEach((kpiValue, index) => {
        setValue(`kpi.${index}`, false);
      });
    }

    const newPurpose2List = purposeList.filter(
      (item) =>
        item.value !== watch('purpose1') && item.value !== watch('purpose3')
    );
    setPurpose2List(newPurpose2List);
    const newPurpose3List = purposeList.filter(
      (item) =>
        item.value !== watch('purpose1') && item.value !== watch('purpose2')
    );
    setPurpose3List(newPurpose3List);
    purposeOtherReset();
  }, [watch('purpose1')]);

  useEffect(() => {
    const newPurpose1List = purposeList.filter(
      (item) =>
        item.value !== watch('purpose2') && item.value !== watch('purpose3')
    );
    setPurpose1List(newPurpose1List);
    const newPurpose3List = purposeList.filter(
      (item) =>
        item.value !== watch('purpose1') && item.value !== watch('purpose2')
    );
    setPurpose3List(newPurpose3List);
    purposeOtherReset();
  }, [watch('purpose2')]);

  useEffect(() => {
    const newPurpose1List = purposeList.filter(
      (item) =>
        item.value !== watch('purpose2') && item.value !== watch('purpose3')
    );
    setPurpose1List(newPurpose1List);
    const newPurpose2List = purposeList.filter(
      (item) =>
        item.value !== watch('purpose1') && item.value !== watch('purpose3')
    );
    setPurpose2List(newPurpose2List);
    purposeOtherReset();
  }, [watch('purpose3')]);

  useEffect(() => {
    !watch('kpi.10') && setValue('kpiOtherText', '');
  }, [watch('kpi.10')]);

  useEffect(() => {
    watch('kpiOtherText') && setValue('kpi.10', true);
  }, [watch('kpiOtherText')]);

  useEffect(() => {
    !watch('menu.99') && setValue('menuOther', '');
  }, [watch('menu.99')]);

  useEffect(() => {
    watch('menuOther') && setValue('menu.99', true);
  }, [watch('menuOther')]);

  const skipRegister = (): void => {
    setLoading(true);
    setTimeout(() => {
      if (currentPage.includes('client')) {
        navigate('/client/auth/complete');
      } else if (currentPage.includes('agent')) {
        navigate('/agent/auth/complete');
      } else {
        navigate('/client/auth/complete');
      }
    }, 800);
  };

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      if (currentUser?.uid) {
        const issue = await submitIssue(currentUser?.uid, watch, issueId);
        const user = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(user);
        const company = doc(
          db,
          'companies',
          userSnap.data()?.companyId as string
        );
        const companySnap = await getDoc(company);
        const issueSnap = await getDoc(issue);
        const newIssueId = issueSnap.data()?.issueId as string;
        const typeText =
          type === 'edit' ? t('mypage.issueEdit') : t('mypage.issueAdd');

        const genreFind =
          genreList.find(
            (element): boolean =>
              element.value === (issueSnap.data()?.genre as number)
          )?.option ?? '';

        const genre: string | undefined =
          select.other === (issueSnap.data()?.genre as number)
            ? (issueSnap.data()?.genreOther as string)
            : genreFind;

        const purposeFind =
          purposeList.find(
            (element): boolean =>
              element.value === (issueSnap.data()?.purpose as number[])[0]
          )?.option ?? '';

        const purposeText: string | undefined =
          select.other === (issueSnap.data()?.purpose as number[])[0]
            ? (issueSnap.data()?.purposeOther as string)
            : purposeFind;

        const budgetFind: string =
          budgetList.find(
            (element): boolean =>
              element.value === (issueSnap.data()?.budget as number)
          )?.option ?? t('consult.label1');

        const budgetText: string = budgetFind;

        let agentName = '';
        if (issueSnap.data()?.agentId as string) {
          const agentUser = doc(
            db,
            'users',
            issueSnap.data()?.agentId as string
          );
          const agentUserSnap = await getDoc(agentUser);
          agentName = (agentUserSnap.data()?.name as string) ?? '';
        }

        const merchandiseType = (issueSnap.data()?.merchandiseType as boolean)
          ? t('issue.merchandiseTrue')
          : t('issue.merchandiseFalse');

        if (
          typeText === t('mypage.issueAdd') &&
          companySnap.exists() &&
          issueSnap.exists() &&
          screen === screenType.client
        ) {
          sendSlackIssueClient(
            companySnap,
            issueSnap,
            typeText,
            genre,
            purposeText,
            budgetText,
            agentName
          );

          await sendMailIssueClient(
            companySnap,
            issueSnap,
            typeText,
            genre,
            purposeText,
            budgetText,
            agentName,
            merchandiseType
          );
        } else if (
          typeText === t('mypage.issueAdd') &&
          companySnap.exists() &&
          issueSnap.exists() &&
          screen === screenType.agent
        ) {
          sendSlackIssueAgent(
            companySnap,
            issueSnap,
            typeText,
            genre,
            purposeText,
            budgetText
          );

          agentName = (userSnap.data()?.name as string) ?? '';

          if (watch('client') !== undefined ?? null ?? '') {
            const client = doc(companiesRef, watch('client') as string);
            const clientUser = await getDoc(client);
            const clientUserSnapName =
              (clientUser.data()?.name as string) ?? '';
            await sendMailIssueAgent(
              companySnap,
              issueSnap,
              typeText,
              genre,
              purposeText,
              budgetText,
              agentName,
              merchandiseType,
              clientUserSnapName
            );
          } else {
            const client = doc(companiesRef, clientCompanyId);
            const clientSnap = await getDoc(client);
            const clientSnapName = (clientSnap.data()?.name as string) ?? '';

            await sendMailIssueAgent(
              companySnap,
              issueSnap,
              typeText,
              genre,
              purposeText,
              budgetText,
              agentName,
              merchandiseType,
              clientSnapName
            );
          }
        }

        window.scrollTo(0, 0);

        if (currentPage.includes('mypage')) {
          if (screen === screenType.client) {
            navigate(
              `/client/mypage/orientation/edit?type=${type}&id=${newIssueId}`
            );
          } else if (screen === screenType.agent) {
            if (type === 'copy' && copyIssueId) {
              navigate(
                `/agent/mypage/orientation/edit?type=${type}&copyId=${copyIssueId}&id=${newIssueId}`
              );
            } else {
              navigate(
                `/agent/mypage/orientation/edit?type=${type}&id=${newIssueId}`
              );
            }
          }
        } else if (screen === screenType.client) {
          navigate(`/client/auth/orientation?type=${type}&id=${newIssueId}`);
        } else if (screen === screenType.agent) {
          navigate('/agent/auth/orientation');
        }
      } else {
        throw new Error();
      }
    } catch (e) {
      throwError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CustomLoading loading={loading} />
      <Grid
        container
        direction="column"
        alignItems="center"
        justifyContent="center"
        sx={{ marginBottom: '24px', marginTop: '24px' }}
      >
        <Grid>
          <Card sx={[styles.cardSize, styles.cardBorder]}>
            <CardContent sx={styles.cardContentPadding}>
              {currentPage.includes(registerIssue) && (
                <CustomStepper steps={steps} activeStep={2} />
              )}
              <Typography
                component="div"
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '16px'
                }}
              >
                <Grid container flexDirection={'row'} alignItems={'center'}>
                  <Typography sx={styles.title} component="div">
                    {currentPage.includes(registerIssue)
                      ? t('registerClient.step3')
                      : type === 'add'
                      ? t('mypage.addIssue')
                      : type === 'edit'
                      ? t('mypage.issueEdit')
                      : type === 'copy'
                      ? t('mypage.issueCopy')
                      : t('mypage.addIssue')}
                  </Typography>
                  <CustomGuideButton
                    title={t('mypage.addIssue')}
                    guideText={t('guideText.issue.addIssue')}
                  />
                </Grid>
                {(currentPage.includes(registerIssue) ||
                  (locationState.state as { skip: boolean })?.skip) && (
                  <Button
                    variant="text"
                    onClick={() => {
                      skipRegister();
                    }}
                    sx={[styles.skipButton, { flex: 'none' }]}
                  >
                    <Typography sx={{ color: blue, fontSize: '14px' }}>
                      {t('issue.skip')}
                    </Typography>
                    <ChevronRightIcon />
                  </Button>
                )}
              </Typography>
              <Typography sx={[styles.issueDescription]} component="div">
                {t('issue.discription')}
              </Typography>
              {screen === screenType.agent && (
                <Controller
                  render={({ field }) => (
                    <CustomSelectField
                      {...field}
                      labelText={t('type.client')}
                      placeholder={t('form.empty')}
                      isRequired
                      requiredText={t('form.required')}
                      error={Boolean(errors.client)}
                      helperText={errors.client?.message}
                      defaultValue={clientCompanyId ?? 0}
                      list={clientList}
                      {...(watch('client') === select.other && {
                        formMb: 'narrow'
                      })}
                      blank
                      {...(type === 'edit' && { disabled: true })}
                    />
                  )}
                  control={control}
                  name="client"
                />
              )}
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    labelText={t('issue.name')}
                    requiredText={t('form.required')}
                    isRequired
                    error={Boolean(errors.name)}
                    helperText={errors.name?.message}
                    multiline
                    rows={3}
                    fieldType="textareaRow3"
                    guideText={t('guideText.issue.name')}
                    value={watch('name') ? watch('name') : undefined}
                  />
                )}
                control={control}
                name="name"
              />

              <Typography component="div" sx={styles.aboutText}>
                {t('issue.aboutPromotion')}
              </Typography>

              <Controller
                render={({ field }) => (
                  <CustomSelectField
                    {...field}
                    labelText={t('issue.genre')}
                    requiredText={t('form.required')}
                    placeholder={t('form.empty')}
                    isRequired
                    error={Boolean(errors.genre)}
                    helperText={errors.genre?.message}
                    defaultValue={0}
                    list={useIndustry()}
                    {...(watch('genre') === select.other && {
                      formMb: 'narrow'
                    })}
                    blank
                  />
                )}
                control={control}
                name="genre"
              />
              {watch('genre') === select.other && (
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={
                        watch('genre') === select.other &&
                        Boolean(errors.genreOther)
                      }
                      helperText={errors.genreOther?.message}
                      isRequired={false}
                      sx={{ marginTop: '0px' }}
                    />
                  )}
                  control={control}
                  name="genreOther"
                />
              )}

              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    labelText={t('issue.productInformation')}
                    requiredText={t('form.required')}
                    isRequired
                    error={Boolean(errors.productInformation)}
                    helperText={errors.productInformation?.message}
                    multiline
                    rows={4}
                    fieldType="textareaRow4"
                    value={
                      watch('productInformation')
                        ? watch('productInformation')
                        : undefined
                    }
                    guideText={t('guideText.issue.productInformation')}
                  />
                )}
                control={control}
                name="productInformation"
              />

              <Controller
                render={({ field }) => (
                  <CustomRedioButton
                    {...field}
                    labelText={t('issue.merchandiseType')}
                    isRequired
                    requiredText={t('form.required')}
                    error={Boolean(errors.merchandiseType)}
                    helperText={errors.merchandiseType?.message}
                    trueOption={t('issue.merchandiseTrue')}
                    falseOption={t('issue.merchandiseFalse')}
                  />
                )}
                control={control}
                name="merchandiseType"
              />

              <Typography component="div" sx={styles.aboutText}>
                {t('issue.aboutPurpose')}
              </Typography>

              <Typography component="div">
                <Grid
                  container
                  flexDirection={'row'}
                  alignItems={'center'}
                  mb={'16px'}
                >
                  <Typography color={gray} sx={styles.label} component="div">
                    {t('issue.purpose')}
                  </Typography>
                  <CustomGuideButton
                    title={t('issue.purpose')}
                    guideText={t('guideText.issue.purpose')}
                  />
                </Grid>
                <Controller
                  render={({ field }) => (
                    <CustomSelectField
                      {...field}
                      labelText={t('issue.purpose1')}
                      placeholder={t('form.empty')}
                      isRequired
                      requiredText={t('form.required')}
                      error={Boolean(errors.purpose1)}
                      helperText={errors.purpose1?.message}
                      defaultValue={0}
                      list={purpose1List}
                      {...(watch('purpose1') === select.other && {
                        formMb: 'narrow'
                      })}
                      blank
                    />
                  )}
                  control={control}
                  name="purpose1"
                />
                {watch('purpose1') === select.other && (
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        isRequired={false}
                        placeholder={t('issue.purposeOther')}
                        error={
                          watch('purpose1') === select.other &&
                          Boolean(errors.purpose1Other)
                        }
                        helperText={errors.purpose1Other?.message}
                        sx={{ marginTop: '0px' }}
                      />
                    )}
                    control={control}
                    name="purpose1Other"
                  />
                )}
                <Controller
                  render={({ field }) => (
                    <CustomSelectField
                      {...field}
                      labelText={t('issue.purpose2')}
                      placeholder={t('form.empty')}
                      isRequired={false}
                      error={Boolean(errors.purpose2)}
                      helperText={errors.purpose2?.message}
                      defaultValue={0}
                      list={purpose2List}
                      {...(watch('purpose2') === select.other && {
                        formMb: 'narrow'
                      })}
                      blank
                    />
                  )}
                  control={control}
                  name="purpose2"
                />
                {watch('purpose2') === select.other && (
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        isRequired={false}
                        placeholder={t('issue.purposeOther')}
                        sx={{ marginTop: '0px' }}
                        error={
                          watch('purpose2') === select.other &&
                          Boolean(errors.purpose2Other)
                        }
                        helperText={errors.purpose2Other?.message}
                      />
                    )}
                    control={control}
                    name="purpose2Other"
                  />
                )}
                <Controller
                  render={({ field }) => (
                    <CustomSelectField
                      {...field}
                      labelText={t('issue.purpose3')}
                      placeholder={t('form.empty')}
                      isRequired={false}
                      error={Boolean(errors.purpose3)}
                      helperText={errors.purpose3?.message}
                      defaultValue={0}
                      list={purpose3List}
                      {...(watch('purpose3') === select.other && {
                        formMb: 'narrow'
                      })}
                      blank
                    />
                  )}
                  control={control}
                  name="purpose3"
                />
                {watch('purpose3') === select.other && (
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        isRequired={false}
                        placeholder={t('issue.purposeOther')}
                        sx={{ marginTop: '0px' }}
                        error={
                          watch('purpose3') === select.other &&
                          Boolean(errors.purpose3Other)
                        }
                        helperText={errors.purpose3Other?.message}
                      />
                    )}
                    control={control}
                    name="purpose3Other"
                  />
                )}
              </Typography>
              <CustomCheckbox
                labelText={t('issue.kpi')}
                isRequired={false}
                error={!watch('kpi.10') && Boolean(errors.kpi)}
                helperText={errors.kpi?.message}
                list={kpi(watch('purpose1'))}
                formMb="extraNarrow"
                guideText={t('guideText.issue.kpi')}
                {...(watch('purpose1') === 0
                  ? {
                      disabled: true
                    }
                  : {
                      disabled: false
                    })}
                control={control}
                name="kpi"
                register={register}
                watch={watch}
              />
              <Typography component="div" sx={{ marginLeft: '25px' }}>
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      isRequired={false}
                      error={watch('kpi.10') && Boolean(errors.kpiOtherText)}
                      helperText={errors.kpiOtherText?.message}
                      multiline
                      rows={3}
                      fieldType="textareaRow3"
                      width="other"
                      disabled={watch('purpose1') === 0}
                      value={
                        watch('kpiOtherText')
                          ? watch('kpiOtherText')
                          : undefined
                      }
                    />
                  )}
                  control={control}
                  name="kpiOtherText"
                />
              </Typography>

              <Typography component="div" sx={styles.aboutText}>
                {t('issue.aboutTarget')}
              </Typography>

              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    labelText={t('issue.targetClient')}
                    isRequired
                    requiredText={t('form.required')}
                    multiline
                    rows={4}
                    fieldType="textareaRow4"
                    error={Boolean(errors.targetClient)}
                    helperText={errors.targetClient?.message}
                    guideText={t('guideText.issue.targetClient')}
                  />
                )}
                control={control}
                name="targetClient"
              />

              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    labelText={t('issue.targetArea')}
                    isRequired={false}
                    error={Boolean(errors.targetArea)}
                    helperText={errors.targetArea?.message}
                    guideText={t('guideText.issue.targetArea')}
                  />
                )}
                control={control}
                name="targetArea"
              />

              <Typography component="div" sx={styles.aboutText}>
                {t('issue.aboutSchedule')}
              </Typography>

              <CustomCheckbox
                labelText={t('issue.startDate')}
                requiredText={t('form.required')}
                isRequired
                error={Boolean(errors.startDateCheck)}
                helperText={errors.startDateCheck?.message}
                list={consult()}
                formMb="narrow"
                control={control}
                name="startDateCheck"
                register={register}
                watch={watch}
                guideText={t('guideText.issue.startDate')}
              />
              <Controller
                render={({ field }) => (
                  <CustomDatePicker
                    {...field}
                    isRequired={false}
                    error={Boolean(errors.startDate)}
                    helperText={errors.startDate?.message}
                    placeholder="2022/07/24"
                    disabled={startDateDisabled}
                    setValue={(newValue) => {
                      setValue('startDate', newValue);
                    }}
                    onChangeForValide={field.onChange}
                  />
                )}
                control={control}
                name="startDate"
              />
              <Typography sx={textMb} align="center" component="div">
                <Typography align="left" component="div">
                  <Grid container flexDirection={'row'} alignItems={'center'}>
                    <Typography color={gray} sx={styles.label} component="span">
                      {t('issue.applicationDate')}
                    </Typography>
                    <Typography component="span" sx={styles.required}>
                      {t('form.required')}
                    </Typography>
                    <CustomGuideButton
                      title={t('issue.applicationDate')}
                      guideText={t('guideText.issue.applicationDate')}
                    />
                  </Grid>
                  <Typography
                    component="div"
                    sx={{ display: 'flex', marginTop: '4px' }}
                  >
                    <Controller
                      render={({ field }) => (
                        <CustomDatePicker
                          {...field}
                          isRequired={false}
                          error={Boolean(errors.applicationDateFrom)}
                          helperText={errors.applicationDateFrom?.message}
                          formMb="narrow"
                          placeholder="2022/07/24"
                          setValue={(newValue) => {
                            setValue('applicationDateFrom', newValue);
                          }}
                          onChangeForValide={field.onChange}
                        />
                      )}
                      control={control}
                      name="applicationDateFrom"
                    />
                    <Typography
                      component="span"
                      sx={{
                        '@media screen and (max-width:480px)': {
                          margin: '8px 5px'
                        },
                        fontSize: '16px',
                        margin: '8px 15px'
                      }}
                    >
                      ~
                    </Typography>
                    <Controller
                      render={({ field }) => (
                        <CustomDatePicker
                          {...field}
                          isRequired={false}
                          error={Boolean(errors.applicationDateTo)}
                          helperText={errors.applicationDateTo?.message}
                          value={field.value}
                          formMb="narrow"
                          placeholder="2022/08/24"
                          setValue={(newValue) => {
                            setValue('applicationDateTo', newValue);
                          }}
                          onChangeForValide={field.onChange}
                        />
                      )}
                      control={control}
                      name="applicationDateTo"
                    />
                  </Typography>
                </Typography>
              </Typography>

              <Typography component="div" sx={styles.aboutText}>
                {t('issue.aboutHowToPromotion')}
              </Typography>

              <CustomCheckbox
                labelText={t('issue.budget')}
                requiredText={t('form.required')}
                isRequired
                error={Boolean(errors.budgetCheck)}
                helperText={errors.budgetCheck?.message}
                formMb="narrow"
                list={consult()}
                control={control}
                name="budgetCheck"
                register={register}
                watch={watch}
              />
              <Controller
                render={({ field }) => (
                  <CustomSelectField
                    {...field}
                    placeholder={t('form.empty')}
                    isRequired={false}
                    error={Boolean(errors.budget)}
                    helperText={errors.budget?.message}
                    defaultValue={0}
                    disabled={budgetDisabled}
                    list={budget()}
                    blank
                  />
                )}
                control={control}
                name="budget"
              />
              <CustomCheckbox
                labelText={t('issue.mediaType')}
                error={!watch('mediaTypeCheck.1') && Boolean(errors.mediaType)}
                helperText={errors.mediaType?.message}
                requiredText={t('form.required')}
                isRequired
                list={consult()}
                formMb="extraNarrow"
                control={control}
                name="mediaTypeCheck"
                register={register}
                watch={watch}
                consultationCheck
              />
              <CustomCheckbox
                isRequired={false}
                list={mediaType()}
                control={control}
                formMb="narrow"
                name="mediaType"
                register={register}
                watch={watch}
                disabled={mediaTypeDisabled}
              />
              <CustomCheckbox
                labelText={t('issue.menu')}
                error={
                  !watch('menuCheck.1') &&
                  !watch('menu.99') &&
                  Boolean(errors.menu)
                }
                helperText={errors.menu?.message}
                requiredText={t('form.required')}
                isRequired
                list={consult()}
                formMb="extraNarrow"
                control={control}
                name="menuCheck"
                register={register}
                watch={watch}
                consultationCheck
              />
              <CustomCheckbox
                isRequired={false}
                list={menu()}
                formMb="narrow"
                control={control}
                name="menu"
                register={register}
                watch={watch}
                disabled={menuDisabled}
              />
              <Typography component="div" sx={{ marginLeft: '25px' }}>
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      isRequired={false}
                      error={watch('menu.99') && Boolean(errors.menuOther)}
                      helperText={errors.menuOther?.message}
                      multiline
                      rows={3}
                      fieldType="textareaRow3"
                      width="other"
                      value={
                        watch('menuOther') ? watch('menuOther') : undefined
                      }
                      disabled={menuDisabled}
                    />
                  )}
                  control={control}
                  name="menuOther"
                />
              </Typography>
              <Controller
                render={({ field }) => (
                  <CustomRedioButton
                    {...field}
                    labelText={t('issue.materialFlg')}
                    isRequired={false}
                    error={Boolean(errors.materialFlg)}
                    helperText={errors.materialFlg?.message}
                    trueOption={t('issue.true')}
                    falseOption={t('issue.false')}
                  />
                )}
                control={control}
                name="materialFlg"
              />
              <Controller
                render={({ field }) => (
                  <CustomRedioButton
                    {...field}
                    labelText={t('issue.castingFlg')}
                    isRequired={false}
                    error={Boolean(errors.castingFlg)}
                    helperText={errors.castingFlg?.message}
                    trueOption={t('issue.true')}
                    falseOption={t('issue.false')}
                    guideText={t('guideText.issue.castingFlg')}
                  />
                )}
                control={control}
                name="castingFlg"
              />
              <CustomCheckbox
                labelText={t('issue.option')}
                isRequired={false}
                error={Boolean(errors.option)}
                helperText={errors.option?.message}
                list={option()}
                control={control}
                name="option"
                register={register}
                watch={watch}
                guideText={t('guideText.issue.option')}
              />
              <CardActions sx={[styles.textMb, { padding: 0 }]}>
                <Grid container direction="column" alignItems="center">
                  <Button
                    disabled={buttonDisabledFlag()}
                    variant="contained"
                    fullWidth
                    sx={[primaryButtonBackground, styles.submitButton]}
                    onClick={() => {
                      void (async () => {
                        setFirstSubmit(true);
                        await handleSubmit(async () => await onSubmit())();
                      })();
                    }}
                  >
                    <Typography
                      component="div"
                      color="white"
                      sx={{ fontSize: 15 }}
                    >
                      {t('issue.issueButton')}
                    </Typography>
                  </Button>
                </Grid>
              </CardActions>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <CustomSnackbar
        open={open}
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
        text={errorText}
      />
    </>
  );
};
export default RegisterIssueCard;
